import Vue from "vue";
import { OrderStatus } from "@/models/order/helpers";
import {
  LineItem,
  LineItemRecord,
  Order,
  OrderDetails
} from "@/models/order/order";
import {
  DriftReport,
  QuoteDraftLine,
  QuoteDraftStatus
} from "@/models/quote_draft/quote_draft";
import { OrderState } from "./types";

export const setLineItem = (
  state: OrderState,
  value: LineItem | LineItemRecord
) => {
  state.items?.push(value as any);
};

export const editLineItem = (
  state: OrderState,
  value: { item: LineItem; index: number }
) => {
  state.items!.splice(value.index, 1, value.item);
};

export const setAllLineItems = (state: OrderState, value: LineItem[] | []) => {
  state.items = value;
};

export const setStatus = (state: OrderState, value: OrderStatus) => {
  state.status = value;
};

export const setNotes = (state: OrderState, value: string) => {
  state.notes = value;
};

export const setOrderId = (
  state: OrderState,
  value: { id: string }
) => {
  state._id = value.id;
};

export const setShipping = (
  state: OrderState,
  value: OrderDetails["shipping_details"]
) => {
  const obj = Object.assign({}, { ...state.shipping_details! }, { ...value });
  state.shipping_details = obj;
};

export const setBilling = (
  state: OrderState,
  value: OrderDetails["billing_details"]
) => {
  const obj = Object.assign({}, { ...state.billing_details! }, { ...value });
  state.billing_details = obj;
};

export const setPayment = (
  state: OrderState,
  value: OrderDetails["payment_details"]
) => {
  const obj = Object.assign({}, { ...state.payment_details! }, { ...value });
  state.payment_details = obj;
};

export const setContact = (
  state: OrderState,
  value: OrderDetails["contact_details"]
) => {
  const obj = Object.assign({}, { ...state.contact_details }, { ...value });
  state.contact_details = obj;
};

export const setOrder = (state: OrderState, value: Order) => {
  const obj = Object.assign({}, { ...state }, { ...value });
  state.billing_details = obj.billing_details;
  state.contact_details = obj.contact_details;
  state.payment_details = obj.payment_details;
  state.shipping_details = obj.shipping_details;
  state._id = obj._id;
  state.notes = obj.notes;
  state.created_type = obj.created_type;
};

export const clearOrder = (state: OrderState, value: Order) => {
  state.billing_details = value.billing_details;
  state.contact_details = value.contact_details;
  state.payment_details = value.payment_details;
  state.shipping_details = value.shipping_details;
  state.notes = "";
  state._id = null;
  delete state.created_type;
  state.netsuite_id = undefined;
  state.netsuite_tran_id = undefined;
  state.draft_id = null;
  state.draft_status = null;
  state.drift_report = null;
};

/**
 * FUPOF-191: hard reset of the whole module. Unlike clearOrder — which leaves
 * the in-flow fields (status, type, status_log, cost_details and the customer
 * block) alone because the same user is still working — this puts every key
 * back to its default. Used when the identity behind the session changes
 * (different user signs in, impersonation is left), where nothing from the
 * previous session may survive into the next one.
 *
 * Keys that aren't in the default state are deleted rather than left in place,
 * so a field written by an older build and still sitting in the persisted
 * localStorage payload can't outlive the reset. `value` must be a fresh deep
 * copy of the defaults — assigning the module-level singleton's nested objects
 * by reference would let the next in-place edit corrupt them.
 */
export const resetState = (state: OrderState, value: OrderState) => {
  Object.keys(state).forEach(key => {
    if (!(key in value)) Vue.delete(state, key);
  });
  Object.keys(value).forEach(key => {
    Vue.set(state, key, (value as any)[key]);
  });
};

export const setDraftMeta = (
  state: OrderState,
  value: {
    draft_id: number | null;
    draft_status: QuoteDraftStatus | null;
    netsuite_id?: number | null;
    netsuite_tran_id?: string | null;
  }
) => {
  state.draft_id = value.draft_id;
  state.draft_status = value.draft_status;
  if (value.netsuite_id !== undefined) {
    state.netsuite_id = value.netsuite_id ?? undefined;
  }
  if (value.netsuite_tran_id !== undefined) {
    state.netsuite_tran_id = value.netsuite_tran_id ?? undefined;
  }
};

/**
 * The NetSuite document number (e.g. "QUO2400") isn't part of the PSQL draft —
 * it only rides along on the NetSuite quote row. The edit flow sets it here so
 * the edit-mode banner can name the quote the way NetSuite does.
 */
export const setNetSuiteTranId = (
  state: OrderState,
  tranId: string | null
) => {
  state.netsuite_tran_id = tranId ?? undefined;
};

/**
 * Merges API-minted uuid / ns_line_id values back onto the store's line items
 * by position. Deliberately a separate mutation from setAllLineItems so that
 * id-stamping isn't treated as a content change (which matters again if the
 * parked draft auto-save plugin is reinstated — see
 * .claude/context/FUPOF-187/FUPOF-191/AUTO-SAVE-PARKED.md).
 */
export const setDraftLineIds = (state: OrderState, lines: QuoteDraftLine[]) => {
  if (!state.items) return;
  state.items = state.items.map((item, i) => ({
    ...item,
    uuid: lines[i]?.uuid ?? item.uuid,
    ns_line_id: lines[i]?.ns_line_id ?? item.ns_line_id
  }));
};

export const setDriftReport = (
  state: OrderState,
  value: DriftReport | null
) => {
  state.drift_report = value;
};

export const setCustomer = (
  state: OrderState,
  value: {
    customer_id: string;
    customer_name: string;
    customer_netsuite_id: number | undefined;
    customer_netsuite_contact_id: number | undefined;
    customer_netsuite_payment_terms_id: number | undefined;
    customer_netsuite_price_level_id: number | undefined;
    customer_netsuite_price_level_discount: number | undefined;
    order_id: string | null;
  }
) => {
  state.customer_id = value.customer_id;
  state.customer_name = value.customer_name;
  state.customer_netsuite_id = value.customer_netsuite_id;
  state.customer_netsuite_contact_id = value.customer_netsuite_contact_id;
  state.customer_netsuite_payment_terms_id =
    value.customer_netsuite_payment_terms_id;
  state.customer_netsuite_price_level_id =
    value.customer_netsuite_price_level_id;
  state.customer_netsuite_price_level_discount =
    value.customer_netsuite_price_level_discount;
  state._id = value.order_id;
};

export const clearCustomer = (state: OrderState, value: null) => {
  state.customer_id = "";
  state.customer_name = "";
  delete state.customer_netsuite_id;
  delete state.customer_netsuite_contact_id;
  delete state.customer_netsuite_payment_terms_id;
  delete state.customer_netsuite_price_level_id;
  delete state.customer_netsuite_price_level_discount;
  state._id = null;
};

export const clearCreatedType = (state: OrderState, value: null) => {
  delete state.created_type;
};
